// Fine here because of popup
import { t } from "i18next"
import toast from "react-hot-toast"
import { getBaseUrl } from "simplydo/core/helpers"
import { getImageUrl, groupCoverImage, ideaCoverImage, userAvatarUrl } from "simplydo/core/images"
import { DefaultPreset } from "simplydo/react/richtext/Remirror/extensions/default"
import type { Preset } from "simplydo/react/richtext/Remirror/types"
import emojiData from "svgmoji/emoji.json"

import api from "api"

const uploadFile = (file, uploadOptions) => {
  const regex = /[^a-zA-Z0-9_.]/g
  const name = file.name.replace(regex, "-")
  const { forType, forId } = uploadOptions

  return new Promise((resolve, reject) => {
    api.uploads.generateFileUploadRequest(
      {
        forType,
        forId,
        name,
        size: file.size,
        type: file.type,
      },
      (response) => {
        const xhr = new XMLHttpRequest()
        xhr.open("PUT", response.signedRequest)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            // progress
          }
        })
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(response.signedDownloadRequest)
            } else {
              try {
                const errorResponse = JSON.parse(xhr.responseText)
                reject(new Error(errorResponse.message || "Unable to upload file"))
              } catch {
                reject(new Error("Unable to upload file"))
              }
            }
          }
        }
        xhr.send(file)
      },
      (err) => {
        toast.error(err.message || "Unable to upload file")
        reject(new Error(err.message || "Unable to upload file"))
      },
    )
  })
}

const FullPreset: Preset = [
  ...DefaultPreset,
  { name: "list" },
  { name: "bulletList" },
  { name: "orderedList" },
  { name: "bold" },
  { name: "strike" },
  { name: "italic" },
  { name: "underline" },
  {
    name: "link",
    config: {
      autoLink: true,
    },
  },
  {
    name: "image",
    config: {
      uploadFile,
      enableResizing: true,
      preferPastedTextContent: false,
    },
  },
  {
    name: "mention",
    config: {
      char: "@",
      name: "users",
      formatMention: (user) => ({
        href: `/users/${user._id}`,
        id: user._id,
        label: user?.profile?.fullName,
        description: user?.profile?.department || user?.profile?.jobTitle || "User",
        image: userAvatarUrl(user),
      }),
    },
  },
  {
    name: "mention",
    config: {
      char: "$",
      name: "ideas",
      formatMention: (idea) => ({
        href: `/ideas/${idea._id}`,
        id: idea._id,
        label: idea?.name,
        description: `${t("common:capitalise", { key: "generic.idea" })} for ${idea?.ownerChallenge?.name}`,
        image: ideaCoverImage(idea),
      }),
    },
  },
  {
    name: "mention",
    config: {
      char: "$",
      name: "challenges",
      formatMention: (challenge) => ({
        href: `/challenges/${challenge._id}`,
        id: challenge._id,
        label: challenge?.name,
        description: `${challenge.stage === "published" ? "Open" : "Closed"} ${t("generic.challenge")}`,
        image: getImageUrl(challenge, ["coverImage", "bannerImage"], ["200x200"]),
      }),
    },
  },
  {
    name: "mention",
    config: {
      char: "$",
      name: "organisations",
      formatMention: (org) => ({
        href: getBaseUrl(org),
        id: org._id,
        label: org?.name,
        description: "Organisation",
        image: org?.darkLogoUrl,
      }),
    },
  },
  {
    name: "mention",
    config: {
      char: "$",
      name: "groups",
      formatMention: (group) => ({
        href: `/groups/${group._id}`,
        id: group._id,
        label: group?.name,
        description: "Group",
        image: groupCoverImage(group),
      }),
    },
  },
  {
    name: "emoji",
    config: {
      data: emojiData,
      moji: "openmoji",
      suggestionCharacter: ":",
      plainText: true,
    },
  },
  { name: "alignment" },
  { name: "video", config: { minWidth: 322, minHeight: 172, resizable: true } },
  { name: "iframe", config: { enableResizing: true } },
  {
    name: "file",
    config: {
      onClick: (src) => {
        window.open(src, "_blank")
      },
      uploadFile,
    },
  },
]

export default FullPreset
